/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import type { UseRequestQueryProps } from "@packages/sdk";
import { useLocalStorageState, useRequestTranscript } from "@packages/sdk";
import type {
  CSSColor,
  HallowElementProps,
  Theme,
  ThemeType,
} from "@packages/ui";
import {
  ChevronRightIcon,
  colors,
  cssMediaQueryMinWidthTablet,
  Loader,
  numericPercentages,
  numericPixels,
  ScrollArea,
  spacing,
  Text,
  useTheme,
} from "@packages/ui";
import type {
  Dispatch,
  MouseEventHandler,
  SetStateAction,
  UIEvent,
} from "react";
import { useCallback, useEffect, useRef } from "react";

import { ReaderThumbnail } from "../../blocks/data/ReaderThumbnail.emotion";
import { OldTextSettings } from "../../blocks/groups/TextSettings/OldTextSettings.emotion";
import type { FontSize } from "./fontStyles.emotion";
import { fontStyles } from "./fontStyles.emotion";

type ScrollStyleRecord = Record<`stage${number}`, Interpolation>;

const bodyStyle: Interpolation = {
  fontFamily: "Hallow Moderat Serif, serif",
  lineHeight: 2,
  marginTop: "210px",
  [cssMediaQueryMinWidthTablet]: {
    marginTop: "280px",
    padding: `${spacing.l} ${spacing.l} ${spacing.mediaPlayerDesktop}`,
  },
  maxWidth: "640px",
  padding: `${spacing.l} ${spacing.l} ${spacing.mediaPlayerMobile}`,
  transitionDuration: "250ms",
  transitionProperty: "font-size, line-height",
  transitionTimingFunction: "ease-out",
  whiteSpace: "pre-line",
  willChange: "background-color",
};
const containerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  placeContent: "flex-start",
  placeItems: "center",
  position: "relative",
};
const immersiveStyle: Interpolation = {
  backgroundColor: "var(--special-color)",
};
const scrollAreaStyle: Interpolation = {
  height: "100vh",
  width: "100vw",
};
const headerStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.background,
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  padding: "76px 0 0",
  [cssMediaQueryMinWidthTablet]: { padding: "144px 0 0" },
  placeContent: "flex-start",
  placeItems: "center",
  pointerEvents: "none",
  position: "fixed",
  transition: "top 250ms ease-out, background 250ms ease-in-out",
  width: numericPercentages[100],
  willChange: "top",
});
const maskStyle: Interpolation = {
  position: "absolute",
  transition: "top 250ms ease-out",
  willChange: "top",
};
const reiterateStyle: Interpolation<CSSColor> = (bgColor) => ({
  "--special-color": bgColor,
});
const footerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  justifyContent: "flex-start",
  marginTop: spacing.l,
};
const reportButtonStyle: Interpolation = {
  alignItems: "center",
  background: colors.transparent,
  border: spacing.none,
  color: "inherit",
  cursor: "pointer",
  display: "flex",
  justifyContent: "flex-start",
  outline: spacing.none,
  padding: spacing.none,
  ":hover": {
    textDecoration: "underline",
  },
  ":focus": {
    outline: spacing.none,
  },
};

const headerStyles: ScrollStyleRecord = {
  stage0: {
    top: 0,
  },
  stage1: {
    top: "-94px",
    [cssMediaQueryMinWidthTablet]: { top: "-162px" },
  },
};

const maskStyles: ScrollStyleRecord = {
  stage0: {
    top: "192px",
    [cssMediaQueryMinWidthTablet]: { top: "258px" },
  },
  stage1: {
    top: "108px",
  },
};

const defaultHeadingStyle: Interpolation = {
  marginBottom: spacing.m,
  marginTop: spacing.m,
  paddingInline: spacing.l,
  textAlign: "center",
};

const defaultThumbnailContainerStyle: Interpolation = {
  display: "flex",
  height: numericPixels[80],
  placeContent: "center",
  placeItems: "flex-end",
  width: numericPixels[80],
};

export type TextOnScreenContentProps = {
  audioId: number | null;
  bodyCSS?: Interpolation;
  headerCSS?: Interpolation;
  headerProps?: HallowElementProps<"header"> & {
    hideSettings?: boolean;
  };
  immersive: "immersive" | "default";
  isCurrentItem: boolean;
  mobileApp?: boolean;
  onAuthError?: (error: Response | DOMException | Error) => void;
  onColorDetermination?: (color: string) => void;
  onReport: MouseEventHandler<HTMLButtonElement>;
  play: () => Promise<void>;
  playing: boolean;
  queryProps?: UseRequestQueryProps;
  scrollStage: 0 | 1;
  setImmersive: Dispatch<SetStateAction<"immersive" | "default">>;
  setScrollStage: Dispatch<SetStateAction<0 | 1>>;
  t: (key: string, values?: any, formats?: any) => string;
  theme?: Theme;
  showDisclaimer?: boolean;
  viewportCSS?: Interpolation;
};

export const TextOnScreenContent = ({
  audioId,
  scrollStage,
  setScrollStage,
  immersive,
  setImmersive,
  playing,
  play,
  isCurrentItem,
  onAuthError,
  onColorDetermination,
  onReport,
  headerCSS = {},
  headerProps: { children: headerChildren, hideSettings, ...headerProps } = {
    children: null,
    hideSettings: false,
    className: "",
  },
  bodyCSS = {},
  t,
  mobileApp = false,
  queryProps = {},
  theme,
  showDisclaimer = true,
  viewportCSS = {},
}: TextOnScreenContentProps) => {
  const [fontSize, setFontSize] = useLocalStorageState<FontSize>({
    key: "tos-size",
    defaultValue: "m",
  });
  const lastScrollTop = useRef<number>(0);
  const { query: transcriptQuery } = useRequestTranscript({
    id: audioId,
    enabled: !!audioId,
    ...queryProps,
  });
  const { renderedTheme } = useTheme();
  if (!theme) theme = renderedTheme;

  const handleScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      // why the typecast? Because UIEvent<T> adds `currentTarget` to the event type as T, but scroll events don't
      // have a currentTarget property
      const scrollTop = (e.target as HTMLDivElement).scrollTop ?? 0;
      if (scrollTop > 75) {
        setScrollStage(1);
      } else {
        setScrollStage(0);
      }
      lastScrollTop.current = scrollTop;
    },
    [scrollStage],
  );

  useEffect(() => {
    if (transcriptQuery?.data && onColorDetermination) {
      onColorDetermination(transcriptQuery.data.images?.color_hex ?? "#171717");
    }
  }, [transcriptQuery?.data, onColorDetermination]);

  if (transcriptQuery.isPending) {
    return <Loader />;
  }

  if (transcriptQuery.isError) {
    console.error(transcriptQuery.error);
    onAuthError?.(transcriptQuery.error);
    return null;
  }

  if (!transcriptQuery.isSuccess) return null;

  if (!audioId) return null;

  const {
    data: { title, images, transcript },
  } = transcriptQuery;

  const thumbnail = (
    <div css={defaultThumbnailContainerStyle}>
      <ReaderThumbnail
        activated={isCurrentItem}
        imageSrc={images}
        playing={playing}
        isHeaderCollapsed={scrollStage === 1}
        title={title}
        onClickCapture={play}
      />
    </div>
  );

  const footer = (
    <div css={footerStyle}>
      <Text type={"body"} size={"s"}>
        {t("transcript_auto_transcribed")}
      </Text>
      <Text type={"body"} size={"s"}>
        •
      </Text>
      <button onClick={onReport} css={reportButtonStyle}>
        <Text type={"body"} size={"s"}>
          {t("transcript_report_concern")}
        </Text>
        <ChevronRightIcon />
      </button>
    </div>
  );

  const body = (
    <article
      css={[
        bodyStyle,
        !mobileApp && fontSize && fontStyles[fontSize as FontSize],
        bodyCSS,
      ]}
    >
      {transcript ?? t("no_transcript_available_text")}
      {transcript && showDisclaimer && footer}
    </article>
  );

  const headingTitle = (
    <div className={`stage${scrollStage}`}>
      <Text
        as={"h1"}
        type={"title"}
        size={scrollStage > 0 ? "xs" : "l"}
        css={defaultHeadingStyle}
      >
        {title}
      </Text>
    </div>
  );

  const header = (
    <header
      css={[
        headerStyle,
        reiterateStyle(images.color_hex ?? "#171717"),
        immersive === "immersive" ? immersiveStyle : null,
        headerStyles[`stage${scrollStage}`],
        headerCSS,
      ]}
      {...headerProps}
    >
      {headerChildren}
      {thumbnail}
      {headingTitle}
    </header>
  );

  const content = (
    <div css={containerStyle}>
      {header}
      {hideSettings ? null : (
        <OldTextSettings
          fontSize={fontSize}
          setFontSize={setFontSize}
          immersive={immersive}
          setImmersive={setImmersive}
          isHeaderCollapsed={scrollStage === 1}
        />
      )}
      {body}
    </div>
  );

  return (
    <ScrollArea
      viewportProps={{ onScroll: handleScroll }}
      maskBottom
      maskTop
      maskTopCSS={[maskStyle, maskStyles[`stage${scrollStage}`]]}
      masksColor={
        immersive === "immersive"
          ? ((images.color_hex ?? "#171717") as `#${string}`)
          : theme === "dark"
            ? "#171717"
            : undefined
      }
      css={scrollAreaStyle}
      scrollbarVerticalCSS={[immersive === "immersive" ? immersiveStyle : null]}
      viewportCSS={viewportCSS}
    >
      {content}
    </ScrollArea>
  );
};
